<template>
  <div class="min-h-screen bg-white">
    <!-- Top Bar -->
    <!-- <TopBar v-if="!isAdminRoute" /> -->
    
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <LazyCart v-if="shouldRenderCart" />
    <ToastNotification
      :visible="isVisible"
      :type="type"
      :title="title"
      :message="message"
      :duration="duration"
      @close="hideToast"
    />
    <ClientOnly>
      <AppUpdateNotice />
    </ClientOnly>
    <ClientOnly>
      <component :is="assetsKitDevBarComponent" v-if="assetsKitDevBarComponent" />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { useToast } from '~/composables/useToast'
import { useCartStore } from '~/stores/cart'
import ToastNotification from '~/components/ToastNotification.vue'
// ErrorBoundary is used in layouts (default.vue) so NuxtPage stays direct child of NuxtLayout
import { useRoute } from 'vue-router'
import { computed, onMounted, onBeforeUnmount, shallowRef, watch, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLocaleHead } from '#i18n'
import {
  SITE_LOCALES,
  MAGAZINE_LOCALES,
  getLocaleFromPath,
  getLocaleLanguage,
  getOgLocale,
  isMagazinePath,
  isSiteLocale,
  stripLocalePrefix
} from '~/utils/locales'

const route = useRoute()
const config = useRuntimeConfig()
const { locale } = useI18n()
const cartStore = useCartStore()
const assetsKitDevBarComponent = shallowRef<Component | null>(null)
let stopCartWatch: (() => void) | null = null
let stopCartSync: (() => void) | null = null
let idleHandle: number | null = null
let fallbackTimer: ReturnType<typeof setTimeout> | null = null
let cartSyncBootstrapPromise: Promise<void> | null = null

const siteUrl = 'https://kendrick.paris'

const normalizePath = (path: string) => {
  if (!path) return '/'
  const sanitized = path.split('?')[0].split('#')[0]
  if (sanitized !== '/' && sanitized.endsWith('/')) {
    return sanitized.slice(0, -1)
  }
  return sanitized
}

const currentPath = computed(() => normalizePath(route.path))
const canonicalUrl = computed(() => `${siteUrl}${currentPath.value}`)
const getCurrentRouteLocale = (fallbackPath: string) => {
  if (process.client) {
    return getLocaleFromPath(`${window.location.pathname}${window.location.search}${window.location.hash}`)
  }

  return getLocaleFromPath(fallbackPath)
}
const currentLocaleCode = computed(() => {
  return getCurrentRouteLocale(route.fullPath || currentPath.value)
})
const ogLocale = computed(() => getOgLocale(currentLocaleCode.value))
const ogLocaleAlternates = computed(() => {
  const locales = isMagazinePath(currentPath.value)
    ? SITE_LOCALES.filter(locale => locale.code === 'en' || locale.code === 'fr')
    : SITE_LOCALES

  return locales
    .filter(locale => locale.code !== currentLocaleCode.value)
    .map(locale => ({
      property: 'og:locale:alternate',
      content: getOgLocale(locale.code)
    }))
})

const initialLocale = currentLocaleCode.value
if (locale.value !== initialLocale) {
  locale.value = initialLocale
}

const localeHead = useLocaleHead({ addSeoAttributes: true })

watch(
  () => route.fullPath,
  (path) => {
    const targetLocale = getCurrentRouteLocale(path || currentPath.value)
    if (locale.value !== targetLocale) {
      locale.value = targetLocale
    }
  },
  { immediate: true }
)

const isAdminRoute = computed(() => {
  const path = route?.path || ''
  const neutralPath = stripLocalePrefix(path)
  return neutralPath === '/app' || neutralPath.startsWith('/app/')
})

// This page already carries its own sticky "Request access" CTA and back
// button; the generic back-to-top would double up with it in the same corner.
const isFounderStoryAccessRoute = computed(() => {
  const neutralPath = stripLocalePrefix(route?.path || '')
  return neutralPath === '/join' || neutralPath.startsWith('/join/') || neutralPath === '/rejoindre' || neutralPath.startsWith('/rejoindre/')
})

const isRegionRestrictedRoute = computed(() => {
  const path = route?.path || ''
  return stripLocalePrefix(path) === '/region-restricted'
})

const shouldRenderCart = computed(() => {
  if (isAdminRoute.value || isRegionRestrictedRoute.value || isFounderStoryAccessRoute.value) return false
  return cartStore.isOpen || cartStore.totalItems > 0
})

const { isVisible, type, title, message, duration, hideToast } = useToast()
const siteTitle = 'Kendrick Paris'
const siteTitleSuffixPattern = /(?:\s+\|\s+Kendrick Paris)+$/i

const formatPageTitle = (titleChunk?: string | null) => {
  const rawTitle = (titleChunk || '').trim()
  if (!rawTitle) return siteTitle

  const dedupedTitle = rawTitle.replace(siteTitleSuffixPattern, ` | ${siteTitle}`)
  if (dedupedTitle.toLowerCase().includes(siteTitle.toLowerCase())) {
    return dedupedTitle
  }

  return `${dedupedTitle} | ${siteTitle}`
}

// Suppress errors and warnings (but allow component logs)
onMounted(() => {
  if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    void import('~/components/AssetsKitDevBar.vue')
      .then(({ default: AssetsKitDevBar }) => {
        assetsKitDevBarComponent.value = AssetsKitDevBar
      })
      .catch(() => {})
  }

  const getSupabaseProjectRef = () => {
    const supabaseUrl = config.public.SUPABASE_URL
    if (!supabaseUrl) return null

    try {
      return new URL(supabaseUrl).hostname.split('.')[0] || null
    } catch {
      return null
    }
  }

  const hasPersistedCart = () => {
    try {
      const raw = window.localStorage.getItem('cart')
      if (!raw) return false
      return raw !== '{}' && raw !== 'null'
    } catch {
      return false
    }
  }

  const hasSupabaseSession = () => {
    const projectRef = getSupabaseProjectRef()
    if (!projectRef) return false

    try {
      return Boolean(window.localStorage.getItem(`sb-${projectRef}-auth-token`))
    } catch {
      return false
    }
  }

  const shouldBootstrapCartSync = () => (
    cartStore.totalItems > 0
    || cartStore.isOpen
    || hasPersistedCart()
    || hasSupabaseSession()
  )

  const bootstrapCartFeatures = async () => {
    if (cartSyncBootstrapPromise) {
      return cartSyncBootstrapPromise
    }

    cartSyncBootstrapPromise = (async () => {
      const { useCartSync } = await import('~/composables/useCartSync')
      const cartSync = useCartSync()
      stopCartSync = cartSync.cleanup || null
    })().catch((error) => {
      cartSyncBootstrapPromise = null
      throw error
    })

    return cartSyncBootstrapPromise
  }

  const scheduleCartBootstrap = () => {
    if (!shouldBootstrapCartSync()) return

    if ('requestIdleCallback' in window) {
      idleHandle = (window as any).requestIdleCallback(() => {
        void bootstrapCartFeatures()
      }, { timeout: 2000 })
      return
    }

    fallbackTimer = setTimeout(() => {
      void bootstrapCartFeatures()
    }, 1200)
  }

  scheduleCartBootstrap()

  stopCartWatch = watch(
    () => [cartStore.totalItems, cartStore.isOpen],
    ([totalItems, isOpen]) => {
      if ((totalItems > 0 || isOpen) && !cartSyncBootstrapPromise) {
        void bootstrapCartFeatures()
      }
    }
  )

  // Don't override console.log - let component logs through
  // Only override console.error and console.warn to suppress specific warnings
  const originalError = console.error
  const originalWarn = console.warn
  
  console.error = (...args: any[]) => {
    // Suppress Suspense warnings in error messages
    if (args[0] && typeof args[0] === 'string' && args[0].includes('Suspense')) {
      return
    }
    // Only log critical errors in production
    if (process.env.NODE_ENV === 'production') {
      const isCritical = args[0] && typeof args[0] === 'string' && 
        (args[0].includes('Critical') || args[0].includes('Fatal'))
      if (isCritical) {
        originalError(...args)
      }
    } else {
      originalError(...args)
    }
  }
  
  console.warn = (...args: any[]) => {
    // Suppress Suspense experimental warnings
    if (args[0] && typeof args[0] === 'string' && args[0].includes('Suspense')) {
      return
    }
    // Suppress experimental feature warnings
    if (args[0] && typeof args[0] === 'string' && args[0].includes('experimental feature')) {
      return
    }
    // Suppress noisy Vue Router "No match found" warnings in dev.
    if (
      args[0] &&
      typeof args[0] === 'string' &&
      (args[0].includes('Vue Router warn') || args[0].includes('[Vue Router warn]')) &&
      args[0].includes('No match found for location with path')
    ) {
      return
    }
    // Suppress all warnings in production
    if (process.env.NODE_ENV === 'production') {
      return
    }
    originalWarn(...args)
  }
  
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    event.preventDefault()
  })
  
  // Handle global errors
  window.addEventListener('error', (event) => {
    event.preventDefault()
  })
})

onBeforeUnmount(() => {
  if (stopCartWatch) {
    stopCartWatch()
    stopCartWatch = null
  }
  if (stopCartSync) {
    stopCartSync()
    stopCartSync = null
  }
  if (idleHandle && 'cancelIdleCallback' in window) {
    (window as any).cancelIdleCallback(idleHandle)
    idleHandle = null
  }
  if (fallbackTimer) {
    clearTimeout(fallbackTimer)
    fallbackTimer = null
  }
})

useHead(() => {
  const magazineRoute = isMagazinePath(currentPath.value)
  const magazineLocaleCodes = new Set(MAGAZINE_LOCALES)
  const magazineOgLocales = new Set(MAGAZINE_LOCALES.map(code => getOgLocale(code)))
  const i18nMeta = (localeHead.value.meta || []).filter((meta) => {
    if (!magazineRoute || meta.property !== 'og:locale:alternate') return true
    return typeof meta.content === 'string' && magazineOgLocales.has(meta.content)
  })
  const i18nLink = (localeHead.value.link || []).filter((link) => {
    if (!magazineRoute || link.rel !== 'alternate' || typeof link.hreflang !== 'string') return true
    if (link.hreflang === 'x-default') return true
    const hreflangLocale = link.hreflang.split('-')[0]
    return isSiteLocale(hreflangLocale) && magazineLocaleCodes.has(hreflangLocale)
  })
  const i18nHtmlAttrs = localeHead.value.htmlAttrs || {}

  const link = [...i18nLink]
  link.push(
    { rel: 'canonical', href: canonicalUrl.value }
  )

  return {
    titleTemplate: formatPageTitle,
    htmlAttrs: {
      ...i18nHtmlAttrs,
      lang: getLocaleLanguage(currentLocaleCode.value)
    },
    meta: [
      ...i18nMeta,
      {
        name: 'language',
        content: currentLocaleCode.value
      },
      {
        name: 'ai-discovery',
        content: 'https://kendrick.paris/ai-discovery.json'
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1.0'
      },
      {
        property: 'og:url',
        content: canonicalUrl.value
      },
      ...ogLocaleAlternates.value,
      {
        property: 'og:locale',
        content: ogLocale.value
      },
      {
        name: 'twitter:url',
        content: canonicalUrl.value
      }
    ],
    link,
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Kendrick Paris',
          url: siteUrl,
          inLanguage: SITE_LOCALES.map(locale => locale.language),
          potentialAction: {
            '@type': 'SearchAction',
            target: `${siteUrl}/magazine?search={search_term_string}`,
            'query-input': 'required name=search_term_string'
          }
        })
      },
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Kendrick',
          alternateName: 'Kendrick Paris',
          url: siteUrl,
          logo: `${siteUrl}/k/kendrick-paris/kendrick-logo.jpg`,
          sameAs: ['https://www.instagram.com/kendrick.paris']
        })
      }
    ]
  }
})
</script>

<style>
html {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  /* Empêcher le zoom sur iOS */
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  text-size-adjust: 100%;
  /* Empêcher le zoom sur double tap */
  touch-action: manipulation;
  /* Empêcher le défilement horizontal */
  overflow-x: hidden;
  overflow-y: auto;
}

/* Empêcher le zoom sur tous les éléments */
* {
  touch-action: manipulation;
}

h1, h2, h3, h4, h5, h6 {
  font-family: 'Playfair Display', Georgia, serif;
}

body {
  @apply antialiased text-gray-900 dark:text-gray-100;
  overflow-x: clip;
  /* S'assurer que la largeur ne dépasse pas la viewport */
  max-width: 100vw;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  @apply bg-gray-100 dark:bg-gray-800;
}

::-webkit-scrollbar-thumb {
  @apply bg-gray-300 dark:bg-gray-600;
}

::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400 dark:bg-gray-500;
}

/* Empêcher le débordement horizontal sur mobile */
@media (max-width: 768px) {
  html {
    overflow-x: hidden !important;
    overflow-y: auto !important;
    max-width: 100vw !important;
    position: relative;
  }

  body {
    overflow: clip !important;
    max-width: 100vw !important;
    position: relative;
  }
  
  /* S'assurer que tous les conteneurs respectent la largeur de l'écran */
  * {
    max-width: 100%;
    box-sizing: border-box;
  }
  
  /* Empêcher les éléments de dépasser */
  .container, .max-w-4xl, .max-w-6xl, .max-w-7xl {
    overflow-x: hidden;
  }
}
</style> 
